import { Controller } from '@hotwired/stimulus';

const INPUT_TYPES = {
  integer: 'number',
  float: 'number',
  date: 'date',
  datetime: 'datetime-local',
};

// A filter bar like GitLab's, see FilterBarComponent: a condition is picked in steps,
// (table >) column > operator > value, then becomes a token. Every change dispatches
// filter-bar:change with the conditions, list values as arrays, valueless ones as null.
export default class extends Controller {
  static targets = ['input', 'draft', 'suggestions', 'template', 'clear'];

  static values = {
    columns: Object, // { column: { type, values } }, with tables: { table: { column: ... } }
    tables: Boolean,
    conditions: Array,
    operators: Object, // { column type: [[operator, label]] }
    listOperators: Array,
    valuelessOperators: Array,
    placeholders: Object, // per step
  };

  connect() {
    this.steps = this.tablesValue
      ? ['table', 'field', 'type', 'value']
      : ['field', 'type', 'value'];
    this.labels = Object.fromEntries(Object.values(this.operatorsValue).flat());
    this.options = [];
    // a page restored from the Turbo cache has its tokens already
    if (this.conditions.length === 0)
      this.conditionsValue.forEach((condition) => this.addToken(condition));
    this.reset();

    // a condition not confirmed with Enter is posted too, the server tells what is missing
    this.finish = this.finish.bind(this);
    this.form = this.element.closest('form');
    this.form?.addEventListener('submit', this.finish);
  }

  disconnect() {
    this.form?.removeEventListener('submit', this.finish);
  }

  get conditions() {
    return [...this.element.querySelectorAll('[data-condition]')].map((token) =>
      JSON.parse(token.dataset.condition)
    );
  }

  // the part the next pick fills
  get step() {
    return this.steps.find((key) => !this.draft[key]);
  }

  get columns() {
    return (this.tablesValue ? this.columnsValue[this.draft.table] : this.columnsValue) || {};
  }

  // a custom.<key> typed by hand is a string
  get column() {
    return this.columns[this.draft.field] || { type: 'string' };
  }

  get listOperator() {
    return this.listOperatorsValue.includes(this.draft.type);
  }

  focus() {
    this.inputTarget.focus();
  }

  suggest() {
    const text = this.inputTarget.value;
    const query = (this.listOperator ? text.split(',').pop() : text).trim().toLowerCase();
    this.options = this.choices().filter(({ value, label }) =>
      [value, label].some((choice) => choice.toLowerCase().includes(query))
    );
    // a typed value is taken as it is, a suggested one only when picked
    this.active = this.step === 'value' ? -1 : 0;
    this.render();
  }

  choices() {
    switch (this.step) {
      case 'table':
        return Object.keys(this.columnsValue).map((table) => ({ value: table, label: table }));
      case 'field': {
        const choices = Object.entries(this.columns)
          .filter(([, { type }]) => this.operatorsValue[type])
          .map(([name, { type }]) => ({ value: name, label: name, hint: type }));
        const typed = this.inputTarget.value.trim();
        if (this.columns.custom && /^custom\.[a-z0-9_]+$/.test(typed) && !this.columns[typed]) {
          choices.unshift({ value: typed, label: typed, hint: 'string' });
        }
        return choices;
      }
      case 'type':
        return (this.operatorsValue[this.column.type] || []).map(([value, label]) => ({
          value,
          label,
        }));
      default:
        return (this.column.values || []).map((value) => ({ value, label: value }));
    }
  }

  render() {
    const items = this.options.map((option, index) => {
      const item = document.createElement('li');
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', index === this.active);
      item.className =
        'flex cursor-pointer justify-between gap-2 px-3 py-1 hover:bg-gray-50 aria-selected:bg-gray-100';
      item.dataset.index = index;
      item.dataset.action = 'mousedown->filter-bar#pick';
      item.textContent = option.label;
      if (option.hint) {
        const hint = document.createElement('span');
        hint.className = 'text-xs text-grayText';
        hint.textContent = option.hint;
        item.append(hint);
      }
      return item;
    });
    this.suggestionsTarget.replaceChildren(...items);
    this.suggestionsTarget.classList.toggle('hidden', items.length === 0);
    items[this.active]?.scrollIntoView({ block: 'nearest' });

    // under the input, inside the bar
    const room = this.element.clientWidth - this.suggestionsTarget.offsetWidth;
    this.suggestionsTarget.style.left = `${Math.max(0, Math.min(this.inputTarget.offsetLeft, room))}px`;
  }

  close() {
    this.options = [];
    this.suggestionsTarget.classList.add('hidden');
  }

  keydown(event) {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();
        if (this.options.length === 0) return this.suggest();
        this.active =
          event.key === 'ArrowDown'
            ? Math.min(this.active + 1, this.options.length - 1)
            : Math.max(this.active - 1, 0);
        return this.render();
      case 'Enter':
        event.preventDefault(); // never submits a form half way through a condition
        if (this.options[this.active]) return this.choose(this.options[this.active]);
        if (this.step === 'value' && this.inputTarget.value.trim())
          return this.add(this.inputTarget.value);
        return;
      case 'Backspace':
        if (this.inputTarget.value) return;
        event.preventDefault();
        return this.back();
      case 'Escape':
        return this.close();
    }
  }

  pick(event) {
    event.preventDefault(); // keeps the focus in the input
    this.choose(this.options[event.currentTarget.dataset.index]);
  }

  choose({ value }) {
    if (this.step !== 'value') {
      this.draft[this.step] = value;
      this.inputTarget.value = '';
      if (this.valuelessOperatorsValue.includes(this.draft.type)) return this.add('');
    } else if (this.listOperator) {
      const picked = this.inputTarget.value.split(',').slice(0, -1);
      this.inputTarget.value = `${this.list([...picked, value]).join(', ')}, `;
    } else {
      return this.add(value);
    }
    this.update();
  }

  // the draft becomes a condition
  add(value) {
    const { type } = this.draft;
    let conditionValue = value.trim();
    if (this.valuelessOperatorsValue.includes(type)) conditionValue = null;
    else if (this.listOperator) conditionValue = this.list(value.split(','));

    this.addToken({ ...this.draft, value: conditionValue });
    this.reset();
    this.changed();
  }

  addToken(condition) {
    const token = this.templateTarget.content.firstElementChild.cloneNode(true);
    token.dataset.condition = JSON.stringify(condition);
    const value = [condition.value ?? ''].flat().join(', ');
    for (const input of token.querySelectorAll('input[data-key]')) {
      input.value = input.dataset.key === 'value' ? value : condition[input.dataset.key] || '';
    }
    const summary = token.querySelector('[data-summary]');
    const field = condition.table ? `${condition.table}.${condition.field}` : condition.field;
    summary.textContent = summary.title = [
      field,
      this.labels[condition.type] || condition.type,
      value,
    ]
      .filter(Boolean)
      .join(' ');
    this.draftTarget.before(token);
  }

  remove(event) {
    event.currentTarget.closest('[data-condition]').remove();
    this.changed();
  }

  // the clear button: drops every condition and the draft
  clear() {
    const hadConditions = this.conditions.length > 0;
    this.element.querySelectorAll('[data-condition]').forEach((token) => token.remove());
    this.reset();
    this.close();
    if (hadConditions) this.changed();
  }

  // Backspace in the empty input: drops the last picked part, or reopens the last condition
  back() {
    const picked = [...this.steps].reverse().find((key) => this.draft[key]);
    if (picked) {
      delete this.draft[picked];
      return this.update();
    }

    const last = this.draftTarget.previousElementSibling;
    if (!last) return;
    const { value, ...draft } = JSON.parse(last.dataset.condition);
    last.remove();
    this.changed();
    this.draft = draft;
    if (this.valuelessOperatorsValue.includes(draft.type)) delete this.draft.type;
    this.update();
    this.inputTarget.value = [value ?? ''].flat().join(', ');
    this.suggest();
  }

  finish() {
    if (this.draft.field) this.add(this.inputTarget.value);
  }

  changed() {
    this.toggleClear();
    this.dispatch('change', { detail: { conditions: this.conditions } });
  }

  toggleClear() {
    const empty = this.conditions.length === 0 && !this.draft.table && !this.draft.field;
    this.clearTarget.classList.toggle('hidden', empty);
  }

  reset() {
    this.draft = {};
    this.inputTarget.value = '';
    this.update();
  }

  // shows the picked parts in front of the input and fits the input to the step
  update() {
    const { table, field, type } = this.draft;
    this.draftTarget.textContent = [
      table && field ? `${table}.${field}` : table || field,
      this.labels[type],
    ]
      .filter(Boolean)
      .join(' ');
    this.draftTarget.classList.toggle('hidden', !(table || field));
    this.toggleClear();

    const step = this.step === 'value' && this.listOperator ? 'list' : this.step;
    this.inputTarget.type = (step === 'value' && INPUT_TYPES[this.column.type]) || 'text';
    this.inputTarget.placeholder = this.placeholdersValue[step];
    if (document.activeElement === this.inputTarget) this.suggest();
  }

  list(values) {
    return values.map((value) => value.trim()).filter(Boolean);
  }
}
